import {Dropdown, Tabs} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {AiOutlineHome} from 'react-icons/ai'
import {FiMoreHorizontal} from 'react-icons/fi'
import {gConfigs, gPaths} from 'views/AppLocals/configs'
import {mapPathParam} from 'views/AppLocals/functions/routerHelper'
import useNavRouteAction from 'views/AppLocals/hooks/useNavRouteAction'
import {NavRoutes} from 'views/AppLocals/hooks/useNavRoutes'
import {notEmpty, NullAsDefault} from 'views/Shared'
import UserContext from '../UserContext'
import {LayoutContext} from '../../../../components/layouts/Default/LayoutContext'

const UserNav = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {isSm, isLg} = useContext(LayoutContext)

    const params = useParams()
    const history = useHistory()
    const id = _.get(params, 'id')
    const rootPath = gPaths.user

    const {
        isValidRoute = NullAsDefault,
        routeEntities = [],
        getDefaultAction = NullAsDefault,
        user = {},
    } = useContext(UserContext)

    const defaultAction = getDefaultAction(rootPath)

    const {currentAction} = useNavRouteAction({
        anchor: id,
        defaultAction,
        isAction: isValidRoute,
    })

    const pathTransform = useCallback(
        (path, segment) =>
            [
                _.isEmpty(id) ? path : mapPathParam(path, {id}),
                segment,
            ]
                .filter(notEmpty)
                .join('/'),
        [id]
    )

    const visibleRoutes = useMemo(
        () =>
            routeEntities.filter(
                ({isInvisible = NullAsDefault}) => !isInvisible({item: user, isSm})
            ),
        [routeEntities, user, isSm]
    )

    const tabItems = useMemo(
        () =>
            visibleRoutes.map(({icon, path, breadcrumbName = NullAsDefault}, index) => ({
                key: path,
                label: index === 0 ? (
                    <span className="flex items-center px-1">
                        <AiOutlineHome size={16}/>
                    </span>
                ) : icon ? (
                    <span className="flex items-center gap-2">
                        {icon} {breadcrumbName(t)}
                    </span>
                ) : breadcrumbName(t),
                children: (
                    <NavRoutes
                        rootPath={rootPath}
                        routes={routeEntities}
                        transform={pathTransform}
                        defaultAction={defaultAction}
                    />
                ),
            })),
        [t, rootPath, defaultAction, visibleRoutes, routeEntities, pathTransform]
    )

    const dropdownItems = useMemo(
        () =>
            visibleRoutes.map(({icon, path, breadcrumbName = NullAsDefault}, index) => ({
                key: path,
                icon: index === 0 ? <AiOutlineHome size={14}/> : icon,
                label: breadcrumbName(t),
            })),
        [t, visibleRoutes]
    )

    return (
        <Wrapper className="flex flex-col">
            <Tabs
                id="tab_content" type="line"
                className="custom-tabs-organization"
                tabBarStyle={gConfigs.styles2.tabBar}
                destroyOnHidden={false}
                onChange={(activeKey) => {
                    history.push({
                        pathname: pathTransform(rootPath, activeKey),
                        search: _.get(history, 'location.search'),
                    })
                }}
                items={tabItems}
                activeKey={currentAction}
                tabBarExtraContent={!isLg && (
                    <Dropdown
                        menu={{
                            items: dropdownItems,
                            selectedKeys: [currentAction],
                            onClick: ({key}) => {
                                history.push({
                                    pathname: pathTransform(rootPath, key),
                                    search: _.get(history, 'location.search'),
                                })
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight">
                        <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-color-50 mx-1 text-color-300">
                            <FiMoreHorizontal size={16}/>
                        </button>
                    </Dropdown>
                )}
            />
        </Wrapper>
    )
}

export default UserNav
